/**
 * The phone-call settings a new TWILIO_VOICE deployment starts from, and the
 * factories that lay stored overrides over them.
 */

import { DEFAULT_TWILIO_REGION } from "../../../../api/domains/deployments/twilio-region.types";
import { DEFAULT_GEMINI_LIVE_MODEL } from "./GeminiLiveModels.schema";
import { DEFAULT_GREETING_PLAYBACK } from "./GreetingPlayback.schema";
import {
  DEFAULT_OPENAI_REALTIME_FEATURE_SETTINGS,
  DEFAULT_OPENAI_REALTIME_MODEL,
  OPENAI_REALTIME_MODELS,
  type OpenAIRealtimeVoice
} from "./OpenAIRealtimeModels.schema";
import { DEFAULT_OPENAI_REALTIME_TURN_DETECTION } from "./OpenAIRealtimeTurnDetection.schema";
import { type PhoneCallDeploymentSettings } from "./PhoneCallDeploymentSettings.schema";
import { DEFAULT_PHONE_CALL_SESSION_TIMEOUT } from "./PhoneCallSessionTimeout.schema";
import { TTS_PROVIDERS, type TtsProvider } from "./TtsProviders.schema";

/** Default TTS provider — OpenAI is the only supported provider via the voice relay. */
const DEFAULT_TTS_PROVIDER: TtsProvider = TTS_PROVIDERS[0]; // "openai"

/** Default voice — sourced from the registry's default model so model + voice stay aligned. */
const DEFAULT_VOICE: OpenAIRealtimeVoice =
  OPENAI_REALTIME_MODELS[DEFAULT_OPENAI_REALTIME_MODEL].defaultVoice;

export const DEFAULT_PHONE_CALL_SETTINGS: PhoneCallDeploymentSettings = {
  phoneNumber: "",
  phonePrice: "0",
  phoneRegion: "US",
  language: "en-US", // BCP-47 — regional variant for accent control (e.g. "fr-FR", "fr-BE")
  ttsProvider: DEFAULT_TTS_PROVIDER,
  voiceId: DEFAULT_VOICE,
  openaiModel: DEFAULT_OPENAI_REALTIME_MODEL,
  openaiSettings: DEFAULT_OPENAI_REALTIME_FEATURE_SETTINGS,
  geminiModel: DEFAULT_GEMINI_LIVE_MODEL,
  greetingPlayback: DEFAULT_GREETING_PLAYBACK,
  // debt: hostRegion lives in deploymentSettings, one copy per deployment, though
  //       it belongs to the phone number (shared across SMS/WhatsApp/Voice on the
  //       same number, and it drives API routing + cost). Ceiling: two deployments
  //       on one number can disagree about its region.
  //       Upgrade trigger: a region move on a shared number that must reach every
  //       deployment on it.
  hostRegion: DEFAULT_TWILIO_REGION,
  vad_threshold: 0.8,
  switch_languages: [],
  openaiTurnDetection: DEFAULT_OPENAI_REALTIME_TURN_DETECTION,
  // No `geminiTurnDetection`: an absent value follows the deployment's MODEL
  // (`resolveGeminiLiveTurnDetection`), through a later model switch included.
  session_timeout: DEFAULT_PHONE_CALL_SESSION_TIMEOUT,
  flow: {
    user_start_first: false,
    interruption: { allowed: true, keep_interruption_message: false },
    response_delay: 0
  },
  transfers: [],
  sendSms: [],
  whitelist: [],
  enableSms: false
};

/**
 * Create Phone Call deployment settings with defaults.
 * Accepts unknown (e.g. raw Prisma JsonValue). Callers should follow up with Zod validation.
 */
export function createPhoneCallDeploymentSettings(
  overrides?: unknown
): PhoneCallDeploymentSettings {
  if (!overrides || typeof overrides !== "object" || Array.isArray(overrides)) {
    return { ...DEFAULT_PHONE_CALL_SETTINGS };
  }
  return { ...DEFAULT_PHONE_CALL_SETTINGS, ...(overrides as Partial<PhoneCallDeploymentSettings>) };
}

/**
 * Create Twilio Voice deployment settings (alias for Phone Call).
 */
export function createTwilioVoiceDeploymentSettings(
  overrides?: unknown
): PhoneCallDeploymentSettings {
  return createPhoneCallDeploymentSettings(overrides);
}
